import QrScanner from 'qr-scanner';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import getCodeType, { CodeType } from 'routes/Scanner/utils/getCodeType';
import getParsedCodes from 'routes/Scanner/utils/getParsedCodes';
import { USER_SELECTED_SCANNER_ACTION } from '@lib/enums/localStorageKeys';
import { ScannerActionType } from 'routes/Scanner/enums';
import { logger } from '@lib/services/logger';
import handleValidCodeForInterventionsAction from 'routes/Scanner/utils/handleValidCodeForInterventionsAction';
import handleValidCodeForCreateInterventionAction from 'routes/Scanner/utils/handleValidCodeForCreateInterventionAction';
import handleIdMode from './handleIdMode';
import handleValidCodeForTasksAction from './handleValidCodeForTasksAction';
import { HandleQrCodeSuccessProps } from './types';

export default async function handleQrCodeSuccess(
  { data }: QrScanner.ScanResult,
  props: HandleQrCodeSuccessProps,
) {
  const {
    id,
    isOnline,
    onOpenCounterIndexFormModal,
    setErrorStatus,
    setLoading,
  } = props;

  const storedSelectedScannerAction =
    (localStorage.getItem(USER_SELECTED_SCANNER_ACTION) as ScannerActionType) ||
    undefined;

  // No code
  if (!data) {
    logger.debug('Scanner: no data in scan result');
    return undefined;
  }

  const codeType = getCodeType(data);

  if (codeType === CodeType.Invalid) {
    logger.warn('Scanner: invalid QR code', { data });
    setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
    return undefined;
  }

  const parsedCodes = getParsedCodes(data);
  const { counterCode, entityCode } = parsedCodes;

  logger.info('Scanner: QR code scanned', {
    data,
    codeType,
    action: storedSelectedScannerAction,
    isOnline,
    parsedCodes,
  });

  setLoading(true);
  switch (storedSelectedScannerAction) {
    case ScannerActionType.AddCounterIndex:
      // Scan process for Counter
      if (counterCode) {
        if (isOnline) {
          logger.debug('Scanner: opening counter index form', {
            entityCode,
            counterCode,
          });
          onOpenCounterIndexFormModal(entityCode, counterCode);
        } else {
          logger.warn('Scanner: counter scan failed - offline');
          setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailedOffline);
        }
      } else {
        logger.warn('Scanner: counter code not found in QR data');
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailedCounter);
      }
      break;
    case ScannerActionType.CreateIntervention:
      if (!isOnline) {
        logger.warn('Scanner: create intervention failed - offline');
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailedOffline);
        break;
      }
      if (counterCode) {
        logger.warn('Scanner: create intervention failed - counter code in QR');
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
        break;
      }
      await handleValidCodeForCreateInterventionAction(
        data,
        props,
        parsedCodes,
        codeType,
      );
      break;
    case ScannerActionType.FindInterventions:
      if (!isOnline) {
        logger.warn('Scanner: find interventions failed - offline');
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailedOffline);
        break;
      }
      if (counterCode) {
        logger.warn('Scanner: find interventions failed - counter code in QR');
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
        break;
      }
      await handleValidCodeForInterventionsAction(
        data,
        props,
        parsedCodes,
        codeType,
      );
      break;
    case ScannerActionType.FindTasks:
      if (counterCode) {
        logger.warn('Scanner: find tasks failed - counter code in QR');
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
        break;
      }
      // Scan process from task page
      if (id) {
        logger.debug('Scanner: handling ID mode', { id });
        handleIdMode(data, props);
        break;
      }
      // Scan process from scanner page
      await handleValidCodeForTasksAction(
        data,
        props,
        parsedCodes,
        codeType,
        isOnline,
      );
      break;
    default:
      logger.warn('Scanner: no action selected', {
        storedSelectedScannerAction,
      });
  }

  setLoading(false);
  return undefined;
}
